import React from 'react';
import { XCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export const MODAL_VARIANTS = {
  blue: 'bg-gradient-to-r from-blue-600 to-indigo-600',
  amber: 'bg-gradient-to-r from-amber-600 to-orange-600',
  emerald: 'bg-gradient-to-r from-emerald-600 to-teal-600',
  green: 'bg-gradient-to-r from-green-600 to-green-700',
  purple: 'bg-gradient-to-r from-purple-600 to-indigo-600',
  indigo: 'bg-gradient-to-r from-indigo-600 to-blue-600',
  slate: 'bg-gradient-to-r from-slate-700 to-slate-900',
  primary: 'bg-gradient-to-r from-primary-600 to-primary-700',
};

const SIZES = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-3xl',
  '2xl': 'sm:max-w-4xl',
  '3xl': 'sm:max-w-5xl',
  '4xl': 'sm:max-w-6xl',
};

const TAB_ACTIVE = {
  blue: 'text-blue-700',
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
  green: 'text-green-700',
  purple: 'text-purple-700',
  indigo: 'text-indigo-700',
  slate: 'text-slate-700',
  primary: 'text-primary-700',
};

function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  icon: Icon,
  variant = 'blue',
  size = 'lg',
  zIndex = 50,
  dir,
  children,
  footer,
  headerContent,
  badges,
  tabs,
  activeTab,
  onTabChange,
  headerActions,
  closeOnOverlay = false,
  className,
  bodyClassName,
  showClose = true,
}) {
  if (!isOpen) return null;

  const gradient = MODAL_VARIANTS[variant] || MODAL_VARIANTS.blue;
  const tabActiveText = TAB_ACTIVE[variant] || TAB_ACTIVE.blue;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ zIndex }}
      dir={dir}
      onClick={closeOnOverlay ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          'bg-white w-full max-h-[92vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden',
          SIZES[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || headerContent) && (
          <div className={cn('px-5 py-4 text-white shrink-0', gradient)}>
            {headerContent || (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {Icon && <Icon className="w-5 h-5 shrink-0 opacity-90" />}
                      <h3 className="text-lg font-semibold truncate">{title}</h3>
                      {headerActions}
                    </div>
                    {subtitle && (
                      <p className="text-sm mt-1 opacity-90 truncate">{subtitle}</p>
                    )}
                    {badges?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {badges.map((badge, index) => (
                          <span
                            key={index}
                            className="text-xs font-medium bg-white/20 px-2.5 py-1 rounded-full"
                          >
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {showClose && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
                      aria-label="Close"
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  )}
                </div>

                {tabs?.length > 0 && (
                  <div className="flex gap-1 mt-4 bg-white/10 p-1 rounded-lg">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => onTabChange?.(tab.id)}
                        className={cn(
                          'flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors',
                          activeTab === tab.id
                            ? cn('bg-white shadow-sm', tabActiveText)
                            : 'text-white/90 hover:bg-white/10'
                        )}
                      >
                        {tab.icon && <tab.icon className="w-4 h-4" />}
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className={cn('flex-1 overflow-y-auto min-h-0', bodyClassName ?? 'p-4 sm:p-5')}>
          {children}
        </div>

        {footer && (
          <div className="shrink-0 border-t border-gray-200 bg-gray-50 px-4 sm:px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ModalSection({ title, icon: Icon, iconClassName, children, className }) {
  return (
    <div className={cn('bg-gray-50 rounded-xl p-4 sm:p-5', className)}>
      {title && (
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          {Icon && <Icon className={cn('w-5 h-5 text-blue-600', iconClassName)} />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}

export function ModalAlert({ type = 'error', children, className }) {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
  };

  return (
    <div className={cn('border rounded-lg px-4 py-3 text-sm', styles[type], className)}>
      {children}
    </div>
  );
}

export function ModalFooterBar({ hint, children, className }) {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      {hint && (
        <p className="text-xs text-gray-500 hidden sm:block truncate">{hint}</p>
      )}
      <div className="flex items-center gap-2 ml-auto shrink-0">{children}</div>
    </div>
  );
}

export default Modal;
